import { useMemo } from 'react';

export const useQuickTracksData = (
  studentBehaviorData,
  user,
  currentUser,
  formState,
  quickTracksSearchTerm,
  quickTracksExpanded,
  sessionData
) => {
  const selectedQuickTrackIds = useMemo(() => {
    if (!Array.isArray(sessionData?.selectedQuickTracks)) return [];
    return sessionData.selectedQuickTracks.filter(Boolean);
  }, [sessionData?.selectedQuickTracks]);

  const selectedQuickTrackIdsSet = useMemo(
    () => new Set(selectedQuickTrackIds),
    [selectedQuickTrackIds]
  );

  const studentQuickTracks = useMemo(() => {
    const studentId = formState?.studentId || user?.studentId || user?.id;
    const currentUserId = currentUser?.uid;

    if (!studentId || !studentBehaviorData || !currentUserId) {
      return [];
    }

    const student = studentBehaviorData.find((s) => s.id === studentId);
    if (!student) {
      return [];
    }

    const quickTracks = [];

    if (Array.isArray(student.behaviors)) {
      student.behaviors.forEach((behavior) => {
        const behaviorId = behavior.id || behavior.docId;
        if (!behaviorId) {
          return;
        }

        const hasStandardAccess = (() => {
          if (!behavior.info || !behavior.info.ShareWithTeam) {
            const isOwner =
              behavior.info?.BehaviorDetails?.BehaviorAuthor === currentUserId;
            return isOwner;
          }

          const { EditAccess = [], ViewOnlyAccess = [] } =
            behavior.info.ShareWithTeam;

          const hasEditAccess = EditAccess.some(
            (access) => access.uid === currentUserId && access.approved === true
          );

          const hasViewAccess = ViewOnlyAccess.some(
            (access) => access.uid === currentUserId && access.approved === true
          );

          const isOwner =
            behavior.info?.BehaviorDetails?.BehaviorAuthor === currentUserId;

          return isOwner || hasEditAccess || hasViewAccess;
        })();

        const hasForcedAccess = selectedQuickTrackIdsSet.has(behaviorId);
        const hasAccess = hasStandardAccess || hasForcedAccess;

        if (!hasAccess) {
          return;
        }

        const behaviorInfo = behavior.info;
        const behaviorDetails = behaviorInfo?.BehaviorDetails;
        const behaviorName =
          behaviorDetails?.Type?.BehaviorName ||
          behaviorDetails?.Type?.SkillName ||
          'Untitled QuickTrack';

        if (behaviorName && behaviorId) {
          quickTracks.push({
            id: behaviorId,
            name: behaviorName,
            type: behaviorDetails?.Type?.Type || 'Behavior',
            behavior,
            isRestricted: !hasStandardAccess && hasForcedAccess,
          });
        }
      });
    }

    return quickTracks;
  }, [
    studentBehaviorData,
    user,
    currentUser?.uid,
    formState,
    selectedQuickTrackIdsSet,
  ]);

  const filteredQuickTracks = useMemo(() => {
    if (!quickTracksSearchTerm) {
      return studentQuickTracks;
    }

    const searchLower = quickTracksSearchTerm.toLowerCase();
    return studentQuickTracks.filter((track) =>
      track.name?.toLowerCase().includes(searchLower)
    );
  }, [studentQuickTracks, quickTracksSearchTerm]);

  const displayedQuickTracks = useMemo(() => {
    return quickTracksExpanded
      ? filteredQuickTracks
      : filteredQuickTracks.slice(0, 10);
  }, [filteredQuickTracks, quickTracksExpanded]);

  const originalSessionQuickTracks = useMemo(() => {
    const quickTracksFromSession =
      sessionData?.originalSessionData?.QuickTracks ||
      sessionData?.originalQuickTrackData ||
      [];
    return Array.isArray(quickTracksFromSession)
      ? quickTracksFromSession
      : [];
  }, [
    sessionData?.originalSessionData?.QuickTracks,
    sessionData?.originalQuickTrackData,
  ]);

  const originalQuickTracksById = useMemo(() => {
    return originalSessionQuickTracks.reduce((acc, item) => {
      if (!item) return acc;
      const quickTrackId =
        item.quickTrackID ||
        item.quickTrackId ||
        item.id ||
        (typeof item === 'string' ? item : null);
      if (!quickTrackId) return acc;
      acc[quickTrackId] = item;
      return acc;
    }, {});
  }, [originalSessionQuickTracks]);

  const selectedQuickTracks = useMemo(() => {
    if (selectedQuickTrackIds.length === 0) {
      return [];
    }

    const seen = new Set();

    return selectedQuickTrackIds.reduce((acc, trackId) => {
      if (!trackId || seen.has(trackId)) {
        return acc;
      }

      seen.add(trackId);

      const accessibleTrack = studentQuickTracks.find(
        (track) => track.id === trackId
      );

      if (accessibleTrack) {
        acc.push(accessibleTrack);
        return acc;
      }

      const originalTrack = originalQuickTracksById[trackId];
      const fallbackName =
        originalTrack?.quickTrackName ||
        originalTrack?.name ||
        `QuickTrack (${trackId})`;

      acc.push({
        id: trackId,
        name: fallbackName,
        type: originalTrack?.type || 'Behavior',
        behavior: null,
        isRestricted: true,
      });

      return acc;
    }, []);
  }, [selectedQuickTrackIds, studentQuickTracks, originalQuickTracksById]);

  return {
    studentQuickTracks,
    filteredQuickTracks,
    displayedQuickTracks,
    selectedQuickTracks,
  };
};

export default useQuickTracksData;

